import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useDispatch } from 'react-redux';
import { updateUI } from 'store/UI/actions';

export const useAudioPermissionCheck = () => {
  const dispatch = useDispatch();
  const [microphonePermissionStatus, setMicrophonePermissionStatus] = useState(null);
  const showAlert  = useAlert();

  const getBrowserInfo = () => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isSafari = (/safari/.test(ua) && !/chrome/.test(ua) && !/chromium/.test(ua)) || 
                     (isIOS && !/crios|fxios/.test(ua));
    const isFirefox = /firefox/.test(ua);
    const isChrome = /chrome/.test(ua) && !/edg/.test(ua);
    const isEdge = /edg/.test(ua);
    const isOpera = /opera|opr/.test(ua);
    
    return {
      isSafari,
      isIOS,
      isFirefox,
      isChrome,
      isEdge,
      isOpera,
      supportsPermissionsAPI: 'permissions' in navigator && !isSafari,
      supportsMediaDevices: 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
    };
  };

  const requestMicrophonePermission = async () => {
    try {
      const browserInfo = getBrowserInfo();

      if (!browserInfo.supportsMediaDevices) {
        showAlert('Microphone access is not supported in this browser.', 'warning');
        return false;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        dispatch(updateUI({ openMicrophonePermissionModal: false }));
        setMicrophonePermissionStatus('granted');
        showAlert('Microphone permission granted successfully!', 'success');
        return true;
      } catch (error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setMicrophonePermissionStatus('denied');
          const safariMessage = browserInfo.isSafari
            ? 'Microphone access was denied. Please allow access in Safari Settings > Websites > Microphone.'
            : 'Microphone access was denied. Please allow access in your browser settings.';
          showAlert(safariMessage, 'error');
          return false;
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setMicrophonePermissionStatus('notfound');
          showAlert('No microphone found. Please connect a microphone.', 'warning');
          return false;
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          setMicrophonePermissionStatus('error');
          const safariMessage = browserInfo.isSafari
            ? 'Microphone is being used by another application. Please close other apps and try again.'
            : 'Unable to access microphone. Please check your device settings.';
          showAlert(safariMessage, 'error');
          return false;
        } else {
          setMicrophonePermissionStatus('error');
          showAlert('Unable to access microphone. Please check your device settings.', 'error');
          return false;
        }
      }
    } catch (error) {
      console.error('Error requesting microphone permission:', error);
      Sentry.captureException(`Error requesting microphone permission: ${error}`);
      showAlert('An error occurred while requesting microphone permission.', 'error');
      return false;
    }
  };

  const checkAudioQuestionsAndRequestPermission = useCallback(async (data) => {
    if (!data?.questions || !Array.isArray(data.questions)) {
      return true;
    }

    const hasAudioQuestion = data.questions.some((question) => {
      const isAudioType =
        question.type === 'audio' ||
        question.type === 'audioEssay' ||
        question.type === 'audioTrueFalse' ||
        question.type === 'audioMultipleChoice' ||
        question.type === 'twoMinAudio' || question?.categoryType?.includes("Audio");

      const hasAudioCategory = question?.categoryType?.includes('Audio') || question?.categoryType === "Audio";

      return isAudioType || hasAudioCategory;
    });

    if (!hasAudioQuestion) {
      return true;
    }

    try {
      const browserInfo = getBrowserInfo();

      if (!browserInfo.supportsMediaDevices) {
        return true;
      }

      if (browserInfo.isSafari) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((track) => track.stop());
          setMicrophonePermissionStatus('granted');
          return true;
        } catch (error) {
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            setMicrophonePermissionStatus('denied');
            dispatch(updateUI({ openMicrophonePermissionModal: true }));
            return false;
          } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
            setMicrophonePermissionStatus('notfound');
            dispatch(updateUI({ openMicrophonePermissionModal: true }));
            return false;
          } else {
            setMicrophonePermissionStatus('error');
            dispatch(updateUI({ openMicrophonePermissionModal: true }));
            return false;
          }
        }
      }

      if (browserInfo.supportsPermissionsAPI) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          
          if (permissionStatus.state === 'denied') {
            setMicrophonePermissionStatus('denied');
            dispatch(updateUI({ openMicrophonePermissionModal: true }));
            return false;
          }
          
          if (permissionStatus.state === 'granted') {
            setMicrophonePermissionStatus('granted');
            return true;
          }
        } catch (permError) {
        }
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        setMicrophonePermissionStatus('granted');
        return true;
      } catch (error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setMicrophonePermissionStatus('denied');
          dispatch(updateUI({ openMicrophonePermissionModal: true }));
          return false;
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setMicrophonePermissionStatus('notfound');
          dispatch(updateUI({ openMicrophonePermissionModal: true }));
          return false;
        } else {
          setMicrophonePermissionStatus('error');
          dispatch(updateUI({ openMicrophonePermissionModal: true }));
          return false;
        }
      }
    } catch (error) {
      Sentry.captureException(`Error checking microphone permission: ${error}`);
      return false;
    }
  }, [dispatch]);

  return {
    requestMicrophonePermission,
    checkAudioQuestionsAndRequestPermission,
    microphonePermissionStatus,
  };
};
